// src/components/Footer.js
import React, { useRef, useEffect } from "react";
import { motion, useAnimation, useInView } from "framer-motion";
import { FaFacebookF, FaTwitter, FaInstagram, FaYoutube } from "react-icons/fa";
import { Code2 } from 'lucide-react';

const Footer = () => {
  const footerRef = useRef(null);
  const controls = useAnimation();
  const isInView = useInView(footerRef, { once: false, margin: "-100px" });

  useEffect(() => {
    if (isInView) {
      controls.start({
        y: [-5, 0, -5],
        transition: { duration: 6, repeat: Infinity, repeatType: "mirror" },
      });
    }
  }, [isInView, controls]);

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const socialHover = (color) => ({
    whileHover: {
      scale: 1.3,
      color: color,
      textShadow: `0 0 8px ${color}`,
      transition: { duration: 0.3 },
    },
  });

  return (
    <footer 
      ref={footerRef} 
      className="bg-gray-900 text-gray-300 relative overflow-hidden"
      style={{ overflowX: 'hidden' }}
    >
      {/* ===== Animated Gradient Wave - FIXED FOR NO HORIZONTAL SCROLL ===== */}
      <motion.div
        className="absolute top-0 left-0 right-0 w-full overflow-hidden leading-[0]"
        style={{ 
          clipPath: 'inset(0)',
          contain: 'layout style paint',
          maxWidth: '100vw'
        }}
        animate={{ scaleY: [1, 1.02, 1], translateY: [0, -3, 0] }}
        transition={{ duration: 6, repeat: Infinity, repeatType: "mirror" }}
      >
        <svg
          className="relative block h-20 sm:h-24 md:h-28"
          style={{ 
            width: '150%',
            minWidth: '150%',
            marginLeft: '-25%'
          }}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#3b82f6" />
              <stop offset="50%" stopColor="#9333ea" />
              <stop offset="100%" stopColor="#f43f5e" />
            </linearGradient>
          </defs>
          <path
            fill="url(#waveGradient)"
            fillOpacity="0.6"
            d="M0,192L80,165.3C160,139,320,85,480,85.3C640,85,800,139,960,154.7C1120,171,1280,149,1360,138.7L1440,128L1440,0L1360,0C1280,0,1120,0,960,0C800,0,640,0,480,0C320,0,160,0,80,0L0,0Z"
          >
            <animate
              attributeName="d"
              dur="15s"
              repeatCount="indefinite"
              values="
                M0,192L80,165.3C160,139,320,85,480,85.3C640,85,800,139,960,154.7C1120,171,1280,149,1360,138.7L1440,128L1440,0L1360,0C1280,0,1120,0,960,0C800,0,640,0,480,0C320,0,160,0,80,0L0,0Z;
                M0,160L80,170C160,180,320,200,480,180C640,160,800,120,960,138C1120,155,1280,165,1360,170L1440,160L1440,0L1360,0C1280,0,1120,0,960,0C800,0,640,0,480,0C320,0,160,0,80,0L0,0Z;
                M0,192L80,165.3C160,139,320,85,480,85.3C640,85,800,139,960,154.7C1120,171,1280,149,1360,138.7L1440,128L1440,0L1360,0C1280,0,1120,0,960,0C800,0,640,0,480,0C320,0,160,0,80,0L0,0Z
              "
            />
          </path>
        </svg>
      </motion.div>

      {/* ===== Floating Shapes - Hidden on mobile for performance ===== */}
      <motion.div 
        className="hidden md:block absolute top-10 left-10 w-32 h-32 bg-blue-700 opacity-10 rounded-full blur-3xl"
        animate={{ y: [0, 20, 0] }}
        transition={{ duration: 8, repeat: Infinity, repeatType: "mirror" }}
      />
      <motion.div 
        className="hidden md:block absolute bottom-10 right-10 w-40 h-40 bg-purple-600 opacity-10 rounded-full blur-3xl"
        animate={{ y: [0, -20, 0] }}
        transition={{ duration: 10, repeat: Infinity, repeatType: "mirror" }}
      />

      {/* ===== Footer Content - FULLY RESPONSIVE ===== */}
      <div className="relative z-10 py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-12 lg:px-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
        
        {/* About Section with Logo - RESPONSIVE */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="flex flex-col items-center sm:items-start"
        >
          {/* Logo - Responsive sizing */}
          <img
            src="/images/fpb.png"
            alt="Federal Polytechnic Bida Logo"
            className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-contain mb-3 sm:mb-4"
          />
          
          {/* About Heading - Responsive text */}
          <h3 className="text-xl sm:text-2xl font-bold mb-2 text-white">About</h3>
          
          {/* About Text - Responsive and truncated */}
          <p className="text-xs sm:text-sm leading-relaxed text-center sm:text-left line-clamp-4">
            The Federal Polytechnic Bida is dedicated to providing quality education and fostering innovation for the future.
          </p>
        </motion.div>

        {/* Quick Links - RESPONSIVE */}
        <motion.div 
          variants={fadeUp} 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true }}
          className="flex flex-col items-center sm:items-start"
        >
          <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-white">Quick Links</h3>
          <ul className="space-y-2 text-center sm:text-left w-full">
            <li>
              <a 
                href="/about" 
                className="hover:text-white transition-colors text-sm sm:text-base block truncate"
              >
                About Us
              </a>
            </li>
            <li>
              <a 
                href="/admissions" 
                className="hover:text-white transition-colors text-sm sm:text-base block truncate"
              >
                Admissions
              </a>
            </li>
            <li>
              <a 
                href="/departments" 
                className="hover:text-white transition-colors text-sm sm:text-base block truncate"
              >
                Departments
              </a>
            </li>
            <li>
              <a 
                href="/contact" 
                className="hover:text-white transition-colors text-sm sm:text-base block truncate"
              >
                Contact
              </a>
            </li>
          </ul>
        </motion.div>

        {/* Contact & Socials - RESPONSIVE */}
        <motion.div 
          variants={fadeUp} 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true }}
          className="flex flex-col items-center sm:items-start sm:col-span-2 lg:col-span-1"
        >
          <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-white">Contact</h3>
          
          {/* Email - Responsive with truncation */}
          <p className="text-xs sm:text-sm mb-2 text-center sm:text-left w-full">
            <span className="block sm:inline">Email: </span>
            <a 
              href="mailto:info@fedpolybida.edu.ng" 
              className="hover:text-white transition-colors break-all sm:break-normal"
            >
              info@fedpolybida.edu.ng
            </a>
          </p>
          
          {/* Phone - Responsive */}
          <p className="text-xs sm:text-sm mb-4 text-center sm:text-left">
            <span className="block sm:inline">Phone: </span>
            <a 
              href="tel:+234123456789" 
              className="hover:text-white transition-colors whitespace-nowrap"
            >
              +234 123 456 789
            </a>
          </p>
          
          {/* Social Icons - Responsive sizing */}
          <div className="flex gap-3 sm:gap-4 mt-2 sm:mt-4">
            <motion.a 
              {...socialHover("#1877F2")} 
              href="#" 
              aria-label="Facebook"
              className="transition-transform"
            >
              <FaFacebookF size={20} className="sm:w-6 sm:h-6" />
            </motion.a>
            <motion.a 
              {...socialHover("#1DA1F2")} 
              href="#" 
              aria-label="Twitter"
              className="transition-transform"
            >
              <FaTwitter size={20} className="sm:w-6 sm:h-6" />
            </motion.a>
            <motion.a 
              {...socialHover("#E1306C")} 
              href="#" 
              aria-label="Instagram"
              className="transition-transform"
            >
              <FaInstagram size={20} className="sm:w-6 sm:h-6" />
            </motion.a>
            <motion.a 
              {...socialHover("#FF0000")} 
              href="#" 
              aria-label="YouTube"
              className="transition-transform"
            >
              <FaYoutube size={20} className="sm:w-6 sm:h-6" />
            </motion.a>
          </div>
        </motion.div>
      </div>

      {/* Bottom Footer - RESPONSIVE */}
        <motion.div 
          className="text-center text-xs sm:text-sm mt-8 sm:mt-12 border-t border-gray-700 pt-4 sm:pt-6 pb-4 sm:pb-6 px-4 relative z-10"
          variants={fadeUp} 
          initial="hidden" 
          whileInView="visible" 
          viewport={{ once: true }}
        >
          <p className="truncate sm:whitespace-normal">
            © {new Date().getFullYear()} Federal Polytechnic Bida. All Rights Reserved. 
          </p>

          <motion.p 
            className="text-gray-400 mt-2 flex items-center justify-center gap-1.5"
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            Developed with
            <motion.span
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="text-purple-400 inline-flex"
            >
              <Code2 size={14} />
            </motion.span>
            by{' '}
            <motion.a 
              href="https://kyrexium-tech-platform.vercel.app/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-semibold bg-clip-text text-transparent bg-[length:200%_auto] hover:opacity-80 transition-opacity"
              style={{
                backgroundImage: "linear-gradient(90deg, #a78bfa, #60a5fa, #a78bfa)",
              }}
              animate={{ backgroundPosition: ["0% center", "200% center"] }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            >
              Kyrexium Tech
            </motion.a>
          </motion.p>
        </motion.div>
    </footer>
  );
};
export default Footer;
